import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import dayjs from 'dayjs';
import { uniqueId } from 'lodash';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { Item, Menu, Separator, useContextMenu } from 'react-contexify';
import { useWatch } from 'react-hook-form';
import {
  Check as ICON_CHECK,
  ChevronLeft as ICON_CHEVRON_LEFT,
  ChevronRight as ICON_CHEVRON_RIGHT,
  X as ICON_X,
} from 'react-feather';
import ReactPlayer from 'react-player';
import { Tooltip } from 'react-tooltip';

// REDUX
import { connect } from 'react-redux';
import { mapDispatchProps, mapStateToProps } from 'store/store';
//
import API from 'api';
import { useLanguage } from '@languages/index';
import { sanitizeFileName } from 'helpers/common';
import { getCurrencyMultiplier } from 'helpers/currency';
import { debounce } from 'helpers/debounce';
import BRAND from 'constants/brand';
import GUIDELINE_RULES from 'constants/guidelineRules';
import { PAYMENT_PRODUCT_TYPE } from 'constants/payment';
import POSTING_VIDEO from 'constants/postingVideo';
import RENTAL from 'constants/rental';

import { CampaignFormContext } from 'pages/campaigns/campaignForm/campaignForm.context';

import BrandFeedbackCreateModal from './components/brandFeedbackCreateModal';
import BrandRequestCreateButton from './components/brandRequestCreateButton';
import ButtonPostingVideoDownload from 'components/buttonPostingVideoDownload';
import FeedbacksModal from './components/feedbacksModal';
import ModalRequestRecruitingCampaignApplicationBrand from 'components/modalBrandRequestRecruitingCampaignApplication';
import ValidationStatusIndicator from './components/validationStatusIndicator/validationStatusIndicator';
import Slider, { makeSettings } from '../slider';
import Button from '../button';

import * as HELPER_CURRENCY from 'helpers/currency';

// CSS
import 'react-tooltip/dist/react-tooltip.css';
import './styles.css';

import { POSTING_VIDEO_DTO_ENUMS } from 'api/repositories/postVideos/postingVideo.constants';
import { BRAND_DTO_ENUMS } from 'api/repositories/brand/brand.constants';


// Add after imports, before interface Props
const STORAGE_KEYS = {
  BRAND_REQUEST_PHONE: 'brandRequestRecruitingCampaignApplication_phone',
  BRAND_REQUEST_SHOULD_SAVE: 'brandRequestRecruitingCampaignApplication_shouldSave',
};


interface Props {
  className?: string;
  onClick?: any;
  style?: any;
  isPopup?: boolean;
  showFeedbacks?: boolean;
  isShoes?: boolean;
  isUnderwear?: boolean;
  posting?: any;
  auth?: any;
  preferences?: any;
  width?: string;
  height?: string;
  brand?: any;
  onUpdate?: (posting?: any) => void;
  onLike?: (userId?: number, result?: boolean) => void;
  onSearch?: (params: any) => void;
  showModal?: (params?: any) => void;
  closeModal?: () => void;
  setModal?: (params?: any) => void;
  showOverlays?: (params?: any) => void;
  closeOverlays?: () => void;
  settings?: any;
}

interface RatingBarPropTypes {
  posting?: any;
  auth?: any;
  onUpdate?: (posting?: any) => void;
}

const FEEDBACK_IMAGE_BASE_URL = 'https://nmodelin20.s3.ap-northeast-2.amazonaws.com/posting/thumb-sm/';
const BEAUTY_REELS_GUIDE_IMAGE_BASE_URL = 'https://nmodelin20.s3.ap-northeast-2.amazonaws.com/guide/reels/';

const PostingCard = (props: Props) => {
  const navigate = useNavigate();

  const language = useLanguage();
  const languageTexts = {
    ...language.common,
    ...language.component.postingCard,
    ...language.guidelineTitles,
    ...language.pages.campaigns,
  };

  const playerRef = useRef<any>(null);
  const [isHovered, setIsHovered] = useState(false);
  const [remainSeconds, setRemainSeconds] = useState<number>(() => {
    const due = dayjs(props.posting?.rental?.inspectionFeedbackDueDt).endOf('day');
    const now = dayjs();
    return Math.max(due.diff(now, 'second'), 0);
  });

  useEffect(() => {
    const due = dayjs(props.posting?.rental?.inspectionFeedbackDueDt).endOf('day');
    const now = dayjs();
    setRemainSeconds(Math.max(due.diff(now, 'second'), 0));
    if (!props.posting?.rental?.inspectionFeedbackDueDt) return;
    const interval = setInterval(() => {
      setRemainSeconds((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [props.posting?.rental?.inspectionFeedbackDueDt]);

  const remainTimeStr = useMemo(() => {
    const days = Math.floor(remainSeconds / (24 * 3600));
    const hours = Math.floor((remainSeconds % (24 * 3600)) / 3600);
    const minutes = Math.floor((remainSeconds % 3600) / 60);
    const seconds = remainSeconds % 60;
    let outputText = `${hours.toString().padStart(2, '0')}`
      + `:${minutes.toString().padStart(2, '0')}`
      + `:${seconds.toString().padStart(2, '0')}`;
    if (days > 0) {
      outputText = `${languageTexts._day(days)} ${outputText}`;
    }
    return outputText;
  }, [remainSeconds]);

  const isPostingOfBeautyBrand = useMemo(() => {
    return props.posting?.brand?.categoryId === BRAND_DTO_ENUMS.CATEGORY_ID.BEAUTY;
  }, [props.posting?.brand?.categoryId]);

  const isWholesale = props.brand.businessType === BRAND.BUSINESS_TYPE.WHOLESALE;
  const CARD_MENU_ID = `product-menu-${uniqueId()}`;
  const { show } = useContextMenu({
    id: CARD_MENU_ID,
  });

  // prettier-ignore
  const VALIDATION_STATUS: { [key: string]: { statusCode: number, message: string } } = {
    // 사전검수 절차
    INSPECTION_INITIAL: { statusCode: 0, message: languageTexts.validation_status_inspection_initial },
    INSPECTION_FIXED: { statusCode: 1, message: languageTexts.validation_status_inspection_fixed },
    INSPECTION_VALIDATING: { statusCode: 2, message: languageTexts.validation_status_inspection_validating },
    // 클린본 절차
    VALIDATING  : { statusCode: 3, message: languageTexts.index_text_38 },
    MODIFYING   : { statusCode: 4, message: languageTexts.index_text_39 },
    REVALIDATING: { statusCode: 5, message: languageTexts.index_text_40 },
    VALIDATED   : { statusCode: 6, message: languageTexts.index_text_37 },
  }

  const [isVisibleFeedbacksModal, setIsVisibleFeedbacksModal] = useState<boolean>(false);
  const [isPartnershipCodeCopied, setIsPartnershipCodeCopied] = useState(false);

  const isRequiredPartnershipCode = useMemo(() => {
    if (!props.posting?.rental?.guidelines) {
      return false;
    }
    if (
      props.posting.rental.guidelines.find((guideline: any) => {
        return (
          guideline.rule?.groupName === GUIDELINE_RULES.GROUP_NAMES.REQUIRE_PARTNERSHIP_CODE &&
          guideline.rule?.value === GUIDELINE_RULES.OPTIONS_REQUIRE_PARTNERSHIP_CODE.REQUIRED
        );
      })
    ) {
      return true;
    }
    return false;
  }, [props.posting?.rental?.guidelines]);

  const downloadablePostingVideos = useMemo(() => {
    return props.posting?.downloadableVideos?.filter((video: any) => {
      return (
        video.contentTypeCode === POSTING_VIDEO_DTO_ENUMS.CONTENT_TYPE_CODE.REELS_RAW ||
        video.contentTypeCode === POSTING_VIDEO_DTO_ENUMS.CONTENT_TYPE_CODE.REELS_PUBLISHED
      );
    });
  }, [props.posting?.downloadableVideos]);

  const displayMenu = (e) => {
    show(e, {
      position: { x: e.clientX - 200, y: e.clientY },
    });
  };

  /** @note 미사용중 (2504~) */
  const RatingBar = (props: RatingBarPropTypes) => {
    if (!props.posting.userId) {
      return null;
    }

    const [rating, setRating] = useState(props.posting.ratingsByBrand ? props.posting.ratingsByBrand.rate : 0);
    const [isUpdated, setIsUpdated] = useState<any>();
    const [isEditing, setIsEditing] = useState<any>(props.posting.ratingsByBrand ? false : true);

    const shadow = {
      border: '0.5px solid rgba(0, 0, 0, 0.1)',
      boxShadow: '0px 8px 8px rgba(0, 0, 0, 0.12)',
    };

    const ratings = [1, 2, 3, 4, 5];

    return (
      <div className="flex w-full flex-row p-4 rounded-sm" style={shadow}>
        {!isEditing && (isUpdated?.ratingsByBrand || props.posting?.ratingsByBrand) ? (
          <div
            className="flex w-full flex-row cursor-pointer"
            onClick={() => {
              setIsEditing(true);
            }}
          >
            <div className="flex flex-row items-center">
              <img className="mr-2" src={`/assets/icons/ic-star-gold.svg`} />
              <span className="text-[32px] tracking-tighter">
                {isUpdated ? isUpdated.ratingsByBrand.rate.toFixed(1) : props.posting.ratingsByBrand.rate.toFixed(1)}
              </span>
            </div>
            <div className="flex flex-col ml-auto justify-center text-right opacity-50 text-[12px]">
              <span>{`@${isUpdated ? isUpdated.ratingsByBrand.adminId : props.posting.ratingsByBrand.adminId}`}</span>
              <span>
                {languageTexts._createdAtDate(
                  isUpdated
                    ? isUpdated.ratingsByBrand.createdAtDateShort
                    : props.posting.ratingsByBrand.createdAtDateShort,
                )}
              </span>
            </div>
          </div>
        ) : (
          <>
            <div className="rating-bar flex w-full flex-row px-3 gap-4 justify-between">
              {ratings.map((rate: number) => {
                return (
                  <div
                    key={`ratingBarStar${uniqueId()}`}
                    className="cursor-pointer"
                    onClick={() => {
                      let targetRating = rate;
                      let isDelete = rating == rate;
                      if (isDelete) {
                        targetRating = 0;
                      }

                      API.PostRating.store({
                        brandToken: props.auth.token,
                        rate: targetRating,
                        user_id: props.posting.userId,
                        posting_id: props.posting.id,
                      })
                        .then(() => {
                          API.Post.show({
                            brandToken: props.auth.token,
                            postingId: props.posting.id,
                          }).then((response: any) => {
                            setIsEditing(false);
                            setRating(isDelete ? 0 : targetRating);
                            setIsUpdated(isDelete ? null : response.data.posting);
                            props.onUpdate ? props.onUpdate(response.data.posting) : null;
                          });
                        })
                        .catch((error) => {});
                    }}
                  >
                    {rating >= rate ? (
                      <img src={`/assets/icons/ic-star-gold.svg`} />
                    ) : (
                      <img src={`/assets/icons/ic-star-gray.svg`} />
                    )}
                  </div>
                );
              })}
            </div>

            <Tooltip anchorSelect=".rating-bar" place="top" content={languageTexts.rating_tooltip} />
          </>
        )}
      </div>
    );
  };

  const showModelRejectModal = (userId: number, isDelete: boolean = false) => {
    debounce(() => {
      props.showModal({
        title: isDelete ? languageTexts.model_reject_title_1 : languageTexts.model_reject_title_2,
        text: isDelete ? languageTexts.model_reject_text_1 : languageTexts.model_reject_text_2,
        cancel: () => {},
        confirm: () => {
          toggleReject(userId);
        },
      });
    }, 400);
  };

  const validationStatus = useMemo((): {
    statusCode: number;
    message: string;
  } => {
    const p = props.posting;

    // 6_VALIDATED
    const isGuidelineValidated = p.rental?.validationValidatedAt != null;
    const isPostingValidated =
      p.rental?.type === RENTAL.TYPE.RENTAL ||
      (p.rental?.type === RENTAL.TYPE.SEEDING && p.confirmPointGivenAt != null);
    if (isGuidelineValidated && isPostingValidated) {
      return VALIDATION_STATUS.VALIDATED;
    }

    const hasInspectionProcess = p.rental?.inspectionFeedbackDueDt != null;

    // 사전검수 전용 절차
    // 0_INSPECTION_INITIAL | 1_INSPECTION_FIXED
    if (hasInspectionProcess) {
      const isInspectionFixedCompleted = (p.rental?.inspectionInspectedAt != null);
      const isInspectionInitialCompleted = (
        dayjs().isAfter(dayjs(p.rental.inspectionFeedbackDueDt), 'day') ||
        p.rental?.inspectionFeedbackAt != null
      );
      if (!isInspectionInitialCompleted) {
        return VALIDATION_STATUS.INSPECTION_INITIAL;
      } else if (!isInspectionFixedCompleted) {
        return VALIDATION_STATUS.INSPECTION_FIXED;
      }
    }

    // 사전검수 이후 기존 절차
    const feedbacks_count = p.rental?.guidelineFeedbacks != null ? p.rental.guidelineFeedbacks.length : -1;
    const feedbacked_at = (p.rental?.guidelineFeedbacks != null && p.rental.guidelineFeedbacks.length > 0)
        ? p.rental.guidelineFeedbacks[0].createdAt
        : null;

    // 2_INSPECTION_VALIDATING | 3_VALIDATING
    if (feedbacks_count <= 0) {
      if (hasInspectionProcess) {
        return VALIDATION_STATUS.INSPECTION_VALIDATING;
      }
      return VALIDATION_STATUS.VALIDATING;
    }
    // 4_MODIFYING
    if (
      p.rental.postings.length <= 1 ||
      Date.parse(p.rental.postings[p.rental.postings.length - 1].reg_dt) <= Date.parse(feedbacked_at)
    ) {
      return VALIDATION_STATUS.MODIFYING;
    }
    // 5_REVALIDATING
    return VALIDATION_STATUS.REVALIDATING;
  }, [props.posting]);

  const validateVideoUrl = (url:string|null) => {
    if (!url) {
      return false;
    }
    const allowedExtensions = [
      '.mp4', '.webm', '.mov', 
      '.avi', '.wmv', '.flv', 
      '.mkv', '.m4v', '.3gp', 
      '.3g2', '.mpg', '.mpeg', 
      '.m2v', '.ogv', '.ts', 
      '.mts', '.m2ts', '.vob'
    ];
    const urlWithoutParams = url.split('?')[0];
    return allowedExtensions.some(ext => 
      urlWithoutParams.toLowerCase().endsWith(ext)
    );
  }

  const downloadImages = (posting) => {
    if (posting.brandId != props.brand.id) {
      props.showModal({
        text: languageTexts.modal_alert_download,
      });
      return false;
    }

    const link = document.createElement('a');
    link.href = `/api/posting/download?brand_token=${props.auth.token}&posting_id=${posting.id}`;
    link.click();
  };

  const toggleReject = (userId: number) => {
    debounce(() => {
      API.Model.toggleReject({
        brandToken: props.auth.token,
        userId: userId,
      })
        .then((response: any) => {
          props.showModal({
            text: response.message,
          });

          API.Post.show({
            brandToken: props.auth.token,
            postingId: props.posting.id,
          }).then((response: any) => {
            props.onUpdate ? props.onUpdate(response.data.posting) : null;
          });
        })
        .catch((error) => {
          props.showModal({
            text: error.message,
          });
        });
    }, 400);
  };

  const openInstagram = (posting) => {
    const url = `${posting.instaUrl}`;

    if (url == 'null') {
      props.showModal({
        text: 'No InstaUrl',
      });
      return;
    }

    const coAuthorValue = props.posting?.rental?.guidelines?.find((guideline: any) => {
      return (
        guideline.rule?.groupName === GUIDELINE_RULES.GROUP_NAMES.REQUIRE_PARTNERSHIP_CODE &&
        guideline.rule?.value === GUIDELINE_RULES.OPTIONS_REQUIRE_PARTNERSHIP_CODE.REQUIRED
      );
    })?.rule?.value;

    const isMobileDevice = () => {
      return /mobile|android|iphone|ipad|phone/i.test(navigator.userAgent);
    };

    if (coAuthorValue) {
      props.showModal({
        maxWidth: 417,
        maxHeight: 304,
        title: languageTexts.create_reels_form_co_author_announcement_title,
        text: languageTexts.create_reels_form_co_author_announcement_desc,
        confirmButtonText: languageTexts.confirm,
        confirm: () => {
          if (isMobileDevice()) {
            window.location.href = url;
          } else {
            window.open(url, '_blank');
          }
        },
      });
      return;
    }

    try {
      if (isMobileDevice()) {
        window.location.href = url;
      } else {
        window.open(url, '_blank');
      }
    } catch (e) {
      props.showModal({
        text: 'No InstaUrl',
      });
    }
  };

  const renderProductContextTab = (posting: any) => {
    return (
      <>
        <div
          className="flex justify-center ml-auto cursor-pointer"
          onClick={(e) => {
            setTimeout(() => {
              displayMenu(e);
            }, 300);
          }}
        >
          <img src={`/assets/icons/ic-dots.svg`} />
        </div>

        <Menu id={`${CARD_MENU_ID}`} animation={false}>
          {!isWholesale && (
            <>
              <Item
                onClick={() => {
                  openInstagram(posting);
                }}
              >
                <div className="flex flex-row justify-center">
                  <span className="text-sm tracking-tighter text-center">
                    {languageTexts.view_on_instagram}
                  </span>
                </div>
              </Item>
              <Separator />
            </>
          )}
          <Item
            onClick={() => {
              props.onSearch
                ? props.onSearch({
                    userId: posting.userId,
                    name: posting.username,
                  })
                : null;
            }}
          >
            <div className="flex flex-row justify-center">
              <span className="text-sm tracking-tighter text-center">
                {languageTexts.view_models_other_posting(props.brand.categoryId)}
              </span>
            </div>
          </Item>
          <Separator />
          <Item
            onClick={() => {
              props.onSearch
                ? props.onSearch({
                    productMasterId: posting.productMasterId,
                    name: posting.name,
                  })
                : null;
            }}
          >
            <div className="flex flex-row justify-center">
              <span className="text-sm tracking-tighter text-center">
                {languageTexts.view_products_other_posting(props.brand.categoryId)}
              </span>
            </div>
          </Item>
          <Separator />
          <Item
            onClick={() => {
              showModelRejectModal(posting.userId, posting.reject);
            }}
          >
            <div className="flex flex-row justify-center">
              <span className="text-sm tracking-tighter text-center">
                {props.brand.isCategoryBeauty
                  ? posting.reject
                    ? languageTexts.model_reject_1
                    : languageTexts.model_reject_2
                  : posting.reject
                  ? languageTexts.model_reject_3
                  : languageTexts.model_reject_4}
              </span>
            </div>
          </Item>

          <Separator />

          {posting.type != 3 ? (
            <Item
              onClick={() => {
                downloadImages(posting);
              }}
            >
              <div className="flex flex-row justify-center">
                <span className="text-sm tracking-tighter text-center">
                  {languageTexts.download}
                </span>
              </div>
            </Item>
          ) : null}

          <Separator />
        </Menu>
      </>
    );
  };

  const renderPostingVideoButtons = () => {
    const CONTENT_TYPE_CODE_ORDERS = [
      POSTING_VIDEO.CONTENT_TYPE_CODE.REELS_PUBLISHED,
      POSTING_VIDEO.CONTENT_TYPE_CODE.REELS_RAW,
      POSTING_VIDEO.CONTENT_TYPE_CODE.REELS_INSPECTION_INITIAL,
      POSTING_VIDEO.CONTENT_TYPE_CODE.REELS_INSPECTION_FIXED,
    ];

    const getPostingVideoButtonsConditionalData = (params: {
      contentTypeCode: number;
      isEnabled: boolean;
      brandCurrency: string;
    }) => {
      const currencyMultiplier = getCurrencyMultiplier(params.brandCurrency);
      switch (params.contentTypeCode) {
        case POSTING_VIDEO.CONTENT_TYPE_CODE.REELS_RAW:
          return {
            title: languageTexts.clean_video_container_title,
            description: params.isEnabled
              ? languageTexts.clean_video_container_description_2
              : languageTexts.clean_video_container_description_1,
            isPaymentRequired: false, // @todo : 클린본 결제 무료로 품 (2025.03.24)
            paymentProductType: PAYMENT_PRODUCT_TYPE.SEEDING_VIDEO_CLEAN,
            paymentPrice: (props?.settings?.priceVideoClean ?? 0) * currencyMultiplier,
          };
        case POSTING_VIDEO.CONTENT_TYPE_CODE.REELS_PUBLISHED:
          return {
            title: languageTexts.published_video_container_title,
            description: params.isEnabled
              ? languageTexts.published_video_container_description_2
              : languageTexts.published_video_container_description_1,
            isPaymentRequired: false,
            paymentProductType: undefined,
            paymentPrice: 0,
          };
        case POSTING_VIDEO.CONTENT_TYPE_CODE.REELS_INSPECTION_INITIAL:
          return {
            title: languageTexts.inspection_initial_video_container_title,
            description: params.isEnabled
              ? languageTexts.inspection_initial_video_container_description_2
              : languageTexts.inspection_initial_video_container_description_1,
            isPaymentRequired: false,
            paymentProductType: undefined,
            paymentPrice: 0,
          };
        case POSTING_VIDEO.CONTENT_TYPE_CODE.REELS_INSPECTION_FIXED:
          return {
            title: languageTexts.inspection_fixed_video_container_title,
            description: params.isEnabled
              ? languageTexts.inspection_fixed_video_container_description_2
              : languageTexts.inspection_fixed_video_container_description_1,
            isPaymentRequired: false,
            paymentProductType: undefined,
            paymentPrice: 0,
          };
        default:
          return {
            title: 'Product type not set',
            description: 'Description not set',
            isPaymentRequired: true,
            paymentProductType: PAYMENT_PRODUCT_TYPE.SEEDING_VIDEO_CLEAN,
            paymentPrice: (props?.settings?.priceVideoClean ?? 0) * currencyMultiplier,
          };
      }
    };

    return [
      ...(downloadablePostingVideos ?? [])
    ]
      .sort((a, b) => {
        return (
          CONTENT_TYPE_CODE_ORDERS.indexOf(a.contentTypeCode) - CONTENT_TYPE_CODE_ORDERS.indexOf(b.contentTypeCode)
        );
      })
      .map((video, index, array) => {
        const hasDownloaded = video.downloadCount != null && video.downloadCount > 0;
        const contentTypeText =
          video.contentTypeCode === POSTING_VIDEO.CONTENT_TYPE_CODE.REELS_RAW
            ? language.database.posting_videos.content_type_code_1
            : video.contentTypeCode === POSTING_VIDEO.CONTENT_TYPE_CODE.REELS_PUBLISHED
            ? language.database.posting_videos.content_type_code_2
            : '';
        const fileName = sanitizeFileName(
          `${props.brand?.name ?? ''}_${props.posting.name}_${props.posting.username}_${contentTypeText}`,
        );
        const isValidated = validationStatus.statusCode == VALIDATION_STATUS.VALIDATED.statusCode;
        const data = getPostingVideoButtonsConditionalData({
          contentTypeCode: video.contentTypeCode,
          isEnabled: isValidated,
          brandCurrency: props.brand.currency,
        });

        
        return (
          <React.Fragment key={video.id}>
            <ButtonPostingVideoDownload
              renderButton={(onClick) => (
                <div
                  className={clsx([
                    'flex flex-1 justify-center items-center py-[15px] cursor-pointer',
                    index < array.length - 1 ? 'border-r border-[#E8E8E8]' : '',
                  ])}
                  onClick={(e) => {
                    onClick(e);
                  }}
                >
                  <div className="flex flex-col w-full justify-center items-center">
                    <div className="text-[17px] font-bold">{data.title}</div>
                    {/* {isValidated && ( */}
                    <div className="mt-[2px] w-full">
                      <div className="flex flex-row text-center justify-center items-center">
                        <img className="w-[24px] h-[24px]" src={`/assets/icons/ic-download-black.svg`} />
                      </div>
                    </div>
                    {/* )} */}
                  </div>
                </div>
              )}
              // REQUEST
              brandToken={props.auth.token}
              // REQUEST-VIDEO
              postingVideoId={video.id}
              fileName={fileName}
              onChangePostingVideo={(postingVideo) => {
                const newPosting = { ...props.posting };
                newPosting.downloadableVideos = newPosting.downloadableVideos.map((v) => {
                  if (v.id == postingVideo.id) {
                    return postingVideo;
                  } else {
                    return v;
                  }
                });
                props.onUpdate ? props.onUpdate(newPosting) : null;
              }}
              // REQUEST-PAYMENT
              isPaymentRequired={data.isPaymentRequired}
              paymentProductType={data.paymentProductType}
              paymentPrice={data.paymentPrice}
              postingId={props.posting.id}
              campaignId={props.posting.campaignId}
              productId={props.posting.productId}
              // FROM REDUX STORE
              showModal={props.showModal}
              showOverlays={props.showOverlays}
              closeOverlays={props.closeOverlays}
            />
          </React.Fragment>
        );
      });
  };

  const renderPartnershipCodeButton = () => {
    if (!isRequiredPartnershipCode) return null;
    
    const isValidated = validationStatus.statusCode == VALIDATION_STATUS.VALIDATED.statusCode;
    if (!isValidated)
      return null;

    const validatedPartnershipCode = props.posting?.validatedPartnershipCode;
    const description = !isValidated
      ? languageTexts.partnership_code_description_not_validated
      : validatedPartnershipCode != null
      ? validatedPartnershipCode
      : languageTexts.partnership_code_description_empty;

    return (
      <div key={'partnership-code-button'} className="w-full flex flex-col justify-center items-center my-[15px]">
        <div className="text-[13px] font-bold">{languageTexts.partnership_code_title}</div>
        <div className="mt-[2px] text-[13px] text-[#999999] break-all px-[20px]">{description}</div>
        {validatedPartnershipCode != null && (
          <div className="mt-[15px] px-[9px] w-full">
            <CopyToClipboard
              onCopy={(copied, result) => {
                let text;
                if (result) {
                  text = languageTexts.copy_result_completed_text;
                  setIsPartnershipCodeCopied(true);
                } else {
                  text = languageTexts.copy_result_failed_text;
                }
                props.showModal({
                  isCompact: true,
                  hideCloseButton: true,
                  text,
                });
              }}
              text={`${props.posting.validatedPartnershipCode}`}
            >
              <Button
                className="flex flex-row py-3 text-center justify-center"
                buttonColor={isPartnershipCodeCopied ? 'WHITE' : 'BLACK'}
                borderColor="BLACK"
                textColor={isPartnershipCodeCopied ? 'BLACK' : 'WHITE'}
                textClassName="text-[12px] font-bold tracking-tighter"
                text={
                  isPartnershipCodeCopied
                    ? languageTexts.copy_button_completed_text
                    : languageTexts.copy_button_default_text
                }
                onClick={(e) => {
                  e.preventDefault();
                }}
              />
            </CopyToClipboard>
          </div>
        )}
      </div>
    );
  };

  const getModelSizeText = (posting: any) => {
    return (
      <>
        {!props.brand.isCategoryBeauty ? `${languageTexts._age(posting.age)}` : null}

        {props.brand.isCategoryFashion
          ? `/ ${languageTexts._height(posting.height)} ` +
            `/ ${languageTexts._top_size(posting.topSize)}, ` +
            `${languageTexts._bottom_size(posting.bottomSize)} `
          : null}

        {props.isUnderwear && posting.braSize && posting.pantieSize
          ? `/ ${languageTexts._bra_size(posting.braSize)} / ${languageTexts._pantie_size(posting.pantieSize)} `
          : null}

        {props.isShoes && posting.shoesSize ? `/ ${languageTexts._shoes_size(props.posting.shoesSize)} / ` : null}
      </>
    );
  };

  const renderLikeButtons = () => {
    const isLiked = props.posting.likes && props.posting.likes.length > 0;

    return (
      <div className="flex flex-row px-2.5 pb-2.5">
        <Button
          className={`flex flex-row py-3 text-center justify-center !rounded-none ${isLiked ? "" : "!border-transparent"}`}
          buttonColor={isLiked ? 'WHITE' : 'BLACK'}
          textColor={isLiked ? 'BLACK' : 'WHITE'}
          textClassName="text-sm tracking-tighter font-bold"
          text={
            isLiked
              ? languageTexts?.brand_like_influencer_1
              : languageTexts?.brand_like_influencer_2
          }
          // icon={
          //   props.posting.likes &&
          //   props.posting.likes.length > 0 && {
          //     position: 'centerRight',
          //     icon: (
          //       <div className="self-center text-white ml-0.5">
          //         <ICON_CHECK
          //           size={16}
          //           color={isLiked ? 'BLACK' : 'WHITE'}
          //           style={{ strokeWidth: '2.5px' }}
          //         />
          //       </div>
          //     ),
          //   }
          // }
          onClick={() => {
            API.Like.store({
              brandToken: props.auth.token,
              subject: 'Users',
              subjectId: props.posting.userId,
            })
              .then((response) => {
                props.onLike ? props.onLike(props.posting.userId, response.data.result) : null;
              })
              .catch((error) => {
                props.showModal({ text: error.message });
              });
          }}
        />
      </div>
    );
  };

  const className = clsx(
    'posting-card relative w-full bg-white',
    `${props.style ? `` : 'md:max-w-[240px]'}`,
    `${props.className ?? ''}`,
    // `${props.isPopup ? 'md:grid md:grid-cols-5' : ''}`,
  );

  const showBrandFeedbackCreateModal = async () => {
    const response = await new Promise((resolve, reject) => {
      props.showModal({
        content: () => {
          return (
            <BrandFeedbackCreateModal
              isVisible={true}
              letClose={(result) => {
                resolve(result);
                props.closeModal();
              }}
              token={props.auth?.token}
              rentalId={props.posting?.rentalId}
              postingId={props.posting?.id}
              showModal={props.showModal}
            />
          );
        },
      });
    });
    if (response) {
      props.showModal({
        hideCloseButton: true,
        title: languageTexts.brand_feedback_modal_2_title,
        text: languageTexts.brand_feedback_modal_2_text,
      });
    }
  };

  const handleBrandRequestRecruitingCampaignApplicationClick = async () => {
  const savedPhone = sessionStorage.getItem(STORAGE_KEYS.BRAND_REQUEST_PHONE);

  // Always show modal (with pre-filled phone if saved)
  const response = await new Promise((resolve) => {
    props.showModal({
      content: () => (
        <ModalRequestRecruitingCampaignApplicationBrand
          isVisible={true}
          letClose={(result) => {
            resolve(result);
            props.closeModal?.();
          }}
          brandToken={props.auth.token}
          brandName={props.brand?.name}
          brandId={props.brand?.id}
          username={props.posting.username}
          savedPhoneNumber={savedPhone || ''}
          userModelSuggestedAt={props.posting.user?.userModel?.suggestedAt}
          userId={props.posting.user?.id}
          mobile={props.posting.user?.mobile}
          userModelInstaUserId={props.posting.user?.userModel?.instaUserId}
          showModal={props.showModal}
          onSavePhoneNumber={(phone, shouldSave) => {
            if (shouldSave) {
              sessionStorage.setItem(STORAGE_KEYS.BRAND_REQUEST_PHONE, phone);
              sessionStorage.setItem(STORAGE_KEYS.BRAND_REQUEST_SHOULD_SAVE, 'true');
            } else {
              sessionStorage.removeItem(STORAGE_KEYS.BRAND_REQUEST_PHONE);
              sessionStorage.removeItem(STORAGE_KEYS.BRAND_REQUEST_SHOULD_SAVE);
            }
          }}
        />
      ),
    });
  });

  if (response) {
    props.showModal({
      hideCloseButton: true,
      title: languageTexts.brand_request_recruiting_campaign_application_modal_success_title,
      text: languageTexts.brand_request_recruiting_campaign_application_modal_success_text,
    });
  }
};


  const renderExposedPostingVideo = (params: {
    exposedVideoUrl: string | null | undefined,
  }) => {
    if (params.exposedVideoUrl == null) {
      return (
        <div className="w-full h-full min-h-[400px] flex flex-col items-center justify-center bg-[#F4F4F4]">
          {renderPostingDueDateInfoA({
            validationStatusCode: validationStatus.statusCode,
            rentalInspectionSubmitDueDt: props.posting?.rental?.inspectionSubmitDueDt,
            rentalReturnDueDt: props.posting?.rental?.returnDueDt,
          })}
        </div>
      );
    }

    const isVideoUrl = validateVideoUrl(params.exposedVideoUrl);
    if (!isVideoUrl) {
      return (
        <img
          className="object-cover w-full h-full min-h-[400px]"
          key={`postingCardImage${uniqueId()}`}
          src={params.exposedVideoUrl}
        />
      );
    }

    return (
      <div
        className="posting-video w-full relative aspect-[9/16]"
        onMouseEnter={() => {
          setIsHovered(true);
          if (playerRef.current) {
            playerRef.current.seekTo(0, 'seconds');
          }
        }}
        onMouseLeave={() => setIsHovered(false)}
      >
        <ReactPlayer
          ref={playerRef}
          // controls
          playing={isHovered}
          url={params.exposedVideoUrl}
          width="100%"
          height="100%"
          className="!absolute !top-0 !left-0 !w-full !h-full object-cover grid"
          style={{ objectFit: 'cover' }}
          config={{
            file: {
              attributes: {
                crossOrigin: 'anonymous',
              },
            },
          }}
        />
      </div>
    );
  }

  const renderPostingImagesSlider = () => {
    return (
      <Slider {...makeSettings({})}>
        {props.posting.images.map((image, idx) => {
          return (
            <div key={idx} className="relative w-full h-full overflow-hidden">
              {image.type == 1 ? (
                <img
                  className="object-cover w-full h-full min-h-[400px]"
                  onClick={() => {
                    if (props.preferences.isMobile) return;

                    props.setModal({
                      isVisible: true,
                      maxWidth: '90vw',
                      className: 'flex flex-col px-5 py-5 md:py-5 bg-white overflow-scroll',
                      renderCustomCloseButton: (closeModal) => (
                        <div
                          className="absolute right-0 top-0 p-2.5 cursor-pointer"
                          onClick={() => {
                            closeModal();
                          }}
                        >
                          <ICON_X strokeWidth={1} color="#000" size={28} />
                        </div>
                      ),
                      content: () => {
                        return (
                          <PostingCard
                            {...props}
                            className="gap-5"
                            isPopup={true}
                            style={{
                              width: '80dvw',
                              maxWidth: '1500px',
                              overflow: 'scroll',
                            }}
                          />
                        );
                      },
                    });
                  }}
                  key={`postingCardImage${uniqueId()}`}
                  src={image.thumbSmUrl}
                />
              ) : null}
              {image.type == 3 ? (
                validateVideoUrl(image?.thumbSmUrl) ? (
                  <div
                    className="posting-video w-full h-[400px] relative"
                    onMouseEnter={() => {
                      setIsHovered(true);
                      if (playerRef.current) {
                        playerRef.current.seekTo(0, 'seconds');
                      }
                    }}
                    onMouseLeave={() => setIsHovered(false)}
                  >
                    <ReactPlayer
                      ref={playerRef}
                      // controls
                      playing={isHovered}
                      key={image?.id}
                      url={image?.thumbSmUrl}
                      width="100%"
                      height="100%"
                      className="!absolute !top-0 !left-0 !w-full !h-full object-cover grid"
                      style={{ objectFit: 'cover' }}
                      config={{
                        file: {
                          attributes: {
                            crossOrigin: 'anonymous',
                          },
                        },
                      }}
                    />
                  </div>
                ) : (
                  <img
                    className="object-cover w-full h-full min-h-[400px]"
                    key={`postingCardImage${uniqueId()}`}
                    src={image.thumbSmUrl}
                  />
                )
              ) : null}
            </div>
          );
        })}
      </Slider>
    );
  }

  const renderPostingDueDateInfoA = (params: {
    validationStatusCode: number | null | undefined,
    rentalInspectionSubmitDueDt: string,
    rentalReturnDueDt: string,
  }) => {
    let dueDate = null;
    let errorText = null;

    if (params.validationStatusCode === VALIDATION_STATUS.INSPECTION_INITIAL.statusCode) {
      if (params.rentalInspectionSubmitDueDt == null) {
        errorText = 'ERROR_NO_RENTAL_INSPECTION_SUBMIT_DUE_DT'; // ERROR CASE
      } else {
        dueDate = dayjs(params.rentalInspectionSubmitDueDt).startOf('day');
      }
    } else if (params.validationStatusCode >= VALIDATION_STATUS.MODIFYING.statusCode) {
      if (params.rentalReturnDueDt == null) {
        errorText = 'ERROR_NO_RENTAL_RETURN_DUE_DT'; // ERROR CASE
      } else {
        dueDate = dayjs(params.rentalReturnDueDt).startOf('day');
      }
    }

    function getDateDiffText(diff: number) {
      if (diff > 0) {
        return languageTexts._dayRemaining(diff);
      } else if (diff === 0) {
        return languageTexts.dayToday;
      } else {
        return languageTexts._dayPassed(-diff);
      }
    }

    if (dueDate != null) {
      const today = dayjs().startOf('day');
      return (
        <div className="flex flex-col items-center text-[#999999] font-bold select-none">
          <div className="text-[13px]">{languageTexts.reels_creation_deadline}</div>
          <div className="text-[17px]">
            {dueDate.format(languageTexts.date_format)}
          </div>
          <div className="text-[13px]">
            {getDateDiffText(dueDate.diff(today, 'day'))}
          </div>
        </div>
      )
    } else if (errorText != null) {
      return (
        <div className="flex flex-col items-center text-[#999999] font-bold">
          <div>{errorText}</div>
        </div>
      )
    }

    return null;
  }
  
  const renderPostingDueDateInfoB = (params: {
    validationStatusCode: number | null | undefined,
    rentalInspectionSubmitDueDt: string,
    rentalReturnDueDt: string,
  }) => {
    let childText = null;

    if (params.validationStatusCode === VALIDATION_STATUS.INSPECTION_INITIAL.statusCode) {
      if (params.rentalInspectionSubmitDueDt == null) {
        childText = 'ERROR_NO_RENTAL_INSPECTION_SUBMIT_DUE_DT'; // ERROR CASE
      } else {
        const dateText = dayjs(params.rentalInspectionSubmitDueDt).format(languageTexts.date_format);
        childText = languageTexts.inspection_created_date(dateText);
      }
    } else if (params.validationStatusCode >= VALIDATION_STATUS.MODIFYING.statusCode) {
      if (params.rentalReturnDueDt == null) {
        childText = 'ERROR_NO_RENTAL_RETURN_DUE_DT'; // ERROR CASE
      } else {
        const dateText = dayjs(params.rentalReturnDueDt).format(languageTexts.date_format);
        childText = languageTexts.inspection_created_date(dateText);
      }
    }

    if (childText != null) {
      return (
        <div className="flex flex-row select-none">
          <span className="text-white text-[11px] tracking-tighter">
            {childText}
          </span>
        </div>
      )
    }

    return null;
  }

  useEffect(() => {
    console.log('[PostingCard] props.posting', props.posting);
  }, [props.posting]);

  return (
    <div
      data-posting-id={props.posting?.id}
      className={className}
      style={props.style ? props.style : {}}
      onClick={() => (props.onClick ? props.onClick() : null)}
    >
      <div className="border border-[#E8E8E8]">
        <div
          className={clsx({
            relative: true,
            'col-span-2 pt-2.5 px-2.5': props.isPopup,
            'overflow-hidden': true,
            // 'h-[393px]': true,
            // 'aspect-[0.7825] overflow-hidden': true,
          })}
        >
          {((props?.posting?.brand ?? props?.brand)?.categoryId === BRAND_DTO_ENUMS.CATEGORY_ID.BEAUTY) ? (
            renderExposedPostingVideo({
              exposedVideoUrl: props?.posting?.derivedData?.exposedVideoUrl,
            })
          ) : (
            renderPostingImagesSlider()
          )}

          {/* Gradient */}
          <div className="pointer-events-none absolute left-0 bottom-0 w-full min-h-[50%] bg-gradient-to-t from-black/50 to-transparent z-0" />

          {/* Instagram Link */}
          {(validationStatus?.statusCode === VALIDATION_STATUS.VALIDATED.statusCode) && (
            <>
              <div className="pointer-events-none absolute left-0 top-0 w-full min-h-[100px] bg-gradient-to-b from-black/50 to-transparent z-0" />
              <div className="absolute flex flex-col items-end gap-2 p-[10px] right-0 left-0 top-0 rounded-sm text-sm">
                <div className="flex flex-row w-full">
                  <div
                    className="flex flex-row bg-white w-full p-[12px] cursor-pointer justify-between items-center"
                    onClick={() => openInstagram(props.posting)}
                  >
                    <div className="flex flex-row items-center gap-[8px]">
                      <img className="w-[18px] h-[18px] cursor-pointer" src={`/assets/icons/ic-instagram.svg`} />

                      <span className="text-black font-bold tracking-tighter">
                        {languageTexts.view_on_instagram}
                      </span>
                    </div>

                    <div className="flex flex-row items-center">
                      <img className="w-4 h-4 cursor-pointer" src={`/assets/icons/ic-arrow-right-black-bold.svg`} />
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          <div className="absolute flex flex-col items-start gap-[5px] p-[10px] left-0 bottom-0 rounded-sm text-sm overflow-hidden z-10">
            {renderPostingDueDateInfoB({
              validationStatusCode: validationStatus.statusCode,
              rentalInspectionSubmitDueDt: props.posting?.rental?.inspectionSubmitDueDt,
              rentalReturnDueDt: props.posting?.rental?.returnDueDt,
            })}
            <div className="flex flex-row">
              <span className="text-white text-[11px] tracking-tighter">
                {`${props.posting?.preferredVideoTypePrefix} ${props.posting?.name}`}
              </span>
            </div>
            {props.posting?.colorName ? (
              <div className="flex flex-row">
                <span className="text-white text-[11px] tracking-tighter">{`${props.posting?.colorName}`}</span>
              </div>
            ) : null}
          </div>

          {/* <div
            className="absolute flex flex-row p-2.5 left-0 right-0 rounded-sm text-sm"
            style={{
              background: `rgba(0,0,0,0.5)`,
            }}
          >
            <div className="flex flex-col">
              <span className="text-white tracking-tighter break-words">{`${props.posting.name}`}</span>
              {props.brand.isCategoryFashion ? (
                <>
                  {props.isShoes ? (
                    <span className="text-white tracking-tighter break-words">
                      {`${props.posting.colorName} / ${languageTexts._wear(props.posting.shoesSize)}`}
                    </span>
                  ) : null}
                  {props.isUnderwear ? (
                    <span className="text-white tracking-tighter break-words">
                      {`${props.posting.colorName} / ${props.posting.braSize} / ${languageTexts._wear(
                        props.posting.pantieSize,
                      )}`}
                    </span>
                  ) : null}
                  {!props.isUnderwear && !props.isShoes ? (
                    <span className="text-white tracking-tighter break-words">
                      {`${props.posting.colorName} / ${languageTexts._wear(props.posting.sizeName)}`}
                    </span>
                  ) : null}
                </>
              ) : (
                <span className="text-white tracking-tighter break-words">{`${props.posting.colorName}`}</span>
              )}
            </div>
            <span className="text-white tracking-tighter ml-auto self-end">{`${props.posting.createdDate}`}</span>
          </div> */}
        </div>

        <div className="flex flex-col col-span-3">
          <div className="flex flex-row justify-center gap-3 px-2.5 py-3.5">
            <div className="border-white border-2 rounded-full overflow-hidden">
              <img
                className="w-[36px] h-[36px] md:w-[48px] md:h-[48px] rounded-full"
                src={`${props.posting.user?.profileImageUrl ?? `/assets/icons/ic-profile-placeholder.svg`}`}
              />
            </div>
            <div className="flex flex-col justify-center gap-1">
              <div className="flex flex-row items-center">
                <div className="mr-1">
                  <div className="text-[17px] font-bold tracking-tighter">
                    {`${props.posting.username}`}
                  </div>
                  <div className="text-[13px] tracking-tighter">
                    <span>
                      {props.posting.user?.suggestions?.length > 0
                        ? languageTexts.model_suggested
                        : languageTexts.model_not_suggested}
                    </span>
                    {(props.posting.applicant?.commissionAmount != null) && (
                      <span>{` · ${HELPER_CURRENCY.makeMoneyText(
                        props.posting.applicant?.commissionAmount, 10000,
                        (moneyText) => languageTexts._price(moneyText, props.settings.currencyText),
                        (moneyText) => languageTexts._price_10000_short(moneyText, props.settings.currencyText),
                      )}`}</span>
                    )}
                  </div>
                </div>

                {/* 운영 쪽 모델 평가 표기 */}
                {/* {props.posting.user && !props.brand.isCategoryBeauty ? (
                  <>
                    <div className="flex items-center mr-0.5">
                      <img src={'/assets/icons/ic-star-active.svg'} />
                    </div>
                    <div>
                      <span className="text-sm tracking-tighter">
                        {props.posting.user.userModel.averageRatingsByAdmin.toFixed(2)}
                      </span>
                    </div>
                  </>
                ) : null} */}
              </div>

              {/* 모델 사이즈들 표기 */}
              {/* <div className="flex flex-row gap-1 text-sm">
                <span className="tracking-tighter opacity-50">{getModelSizeText(props.posting)}</span>
              </div> */}
            </div>
            {renderProductContextTab(props.posting)}
          </div>

          {renderLikeButtons()}

          {/* 포스팅 사전 검수 여부 인디케이터 */}
          {props.posting?.hasInspectionProcess ? (
            <div className="flex flex-row justify-center border-t border-[#E8E8E8] ">
              <div className={'flex flex-row justify-center py-5'}>
                <span className={'font-bold break-keep text-[17px]'}>{languageTexts.inspection_campaign_title}</span>
              </div>
            </div>
          ) : null}

          {/* 포스팅 사전 검수 남은 시각 타이머 */}
          {props.posting?.hasInspectionProcess ? (
            props.posting?.isInspectionProcessCompleted ? (
              <div
                className="px-2.5 pb-2.5 cursor-pointer"
                onClick={() => {
                  navigate(`/postings/${props.posting.id}/feedback`);
                }}
              >
                <div className={'flex flex-row justify-center py-3 border border-[#E8E8E8]'}>
                  <span className={'font-bold break-keep text-sm text-[#E8E8E8]'}>
                    {languageTexts.inspection_status_completed}
                  </span>
                </div>
              </div>
            ) : (
              <div className="px-2.5 pb-2.5 cursor-pointer"
                onClick={() => {
                  navigate(`/postings/${props.posting.id}/feedback`);
                }}
              >
                <div className={'flex flex-col items-center py-3 bg-black'}>
                  <div className={'font-bold break-keep text-sm text-white'}>
                    {languageTexts.inspection_remain_time(remainTimeStr)}
                  </div>
                  <div className="text-white text-[12px]">
                    {`${languageTexts.deadline} ${dayjs(props.posting?.rental?.inspectionFeedbackDueDt).format('YY.MM.DD')}`}
                  </div>
                </div>
              </div>
            )
          ) : null}

          {(downloadablePostingVideos ?? [])?.length > 0 ? (
            <div className={'flex flex-row border-t border-b border-[#E8E8E8] items-stretch'}>
              {...[...renderPostingVideoButtons()]}
            </div>
          ) : null}

          {isRequiredPartnershipCode ? renderPartnershipCodeButton() : null}

          {/* 포스팅 검수 상태 */}
          <ValidationStatusIndicator
            validationStatusCode={validationStatus.statusCode}
            validationStatusMessage={validationStatus.message}
            openFeedbacksModal={() => {
              setIsVisibleFeedbacksModal(true);
            }}
          />
            {/* {props.posting.brandId == props.brand.id && (
            <div className="flex flex-row">
              <RatingBar {...props} posting={props.posting} />
            </div>
          )} */}
        </div>
      </div>

      {/* 이 릴스 또는 인플루언서에 불만족하셨나요? | 원본 및 클린본 파일이 당장 필요하신가요? */}
      {(props.brand.isCategoryBeauty && validationStatus.statusCode < VALIDATION_STATUS.VALIDATED.statusCode) ? (
        <BrandRequestCreateButton posting={props.posting} />
      ) : (props.brand.isCategoryBeauty && validationStatus.statusCode === VALIDATION_STATUS.VALIDATED.statusCode) ? (
        <div
          className="mt-[14px] text-[13px] text-[#999] text-center underline cursor-pointer"
          onClick={() => showBrandFeedbackCreateModal()}
        >
          {languageTexts.brand_feedback_btn_text}
        </div>
      ) : null}

      {/* 우리 캠페인에 지원해 달라고 부탁하기 */}
      {props.brand.isCategoryBeauty && validationStatus.statusCode === VALIDATION_STATUS.VALIDATED.statusCode && (
        <div
          className="-mt-[9px] text-[13px] text-[#999] text-center underline cursor-pointer"
          onClick={handleBrandRequestRecruitingCampaignApplicationClick}   
        >         
          {languageTexts?.brand_request_recruiting_campaign_application_btn_text}
        </div>
      )}

      {isVisibleFeedbacksModal === null ? null : (
        <FeedbacksModal
          isVisible={isVisibleFeedbacksModal === true}
          letClose={() => setIsVisibleFeedbacksModal(false)}
          isCategoryBeautyBrand={props.brand.isCategoryBeauty}
          token={props.auth.token}
          rentalId={props.posting.rentalId}
        />
      )}
    </div>
  );
};

export default connect(mapStateToProps, mapDispatchProps)(PostingCard);
